<template>
  <div class="nosto-range-slider">
    <div class="nosto-range-slider__inputs">
      <div class="nosto-range-slider__inputs-wrapper">
        <span
          class="nosto-range-slider__inputs-currency text-body-xs-desktop text-body-2xs"
          v-text="$variable('store.currencySymbol')"
        />
        <input
          :value="setMin"
          :aria-label="$string('collection.price_min')"
          class="nosto-range-slider__inputs-input text-body-xs-desktop text-body-2xs"
          js-nosto-range-slider="min-text"
          min="0"
          :max="setMax"
          type="text"
          @input="handleTextInput"
        >
      </div>

      <div class="nosto-range-slider__inputs-divider" />

      <div class="nosto-range-slider__inputs-wrapper">
        <span
          class="nosto-range-slider__inputs-currency text-body-xs-desktop text-body-2xs"
          v-text="$variable('store.currencySymbol')"
        />
        <input
          :value="setMax"
          :aria-label="$string('collection.price_max')"
          class="nosto-range-slider__inputs-input text-body-xs-desktop text-body-2xs"
          js-nosto-range-slider="max-text"
          :min="setMin"
          :max="maxCalculated"
          type="text"
          @input="handleTextInput"
        >
      </div>
    </div>

    <!-- Range slider -->
    <div
      class="nosto-range-slider__wrap"
      role="group"
      aria-labelledby="multi-lbl"
      :style="
        `--range-thumb-left: ${setMin};
        --range-thumb-right: ${setMax};
        --range-min: 0;
        --range-max: ${maxCalculated}`
      "
    >
      <label
        class="visually-hidden"
        for="range-slider-left"
        v-text="$string('collection.price_min')"
      />
      <input
        :aria-label="$string('collection.price_min_slider')"
        name="range-slider-left"
        data-range-side="left"
        js-nosto-range-slider="input"
        type="range"
        min="0"
        :value="setMin"
        :max="maxCalculated"
        step="1"
        @touchend="handleRangeTouch"
      >

      <label
        class="visually-hidden"
        for="range-slider-right"
        v-text="$string('collection.price_max')"
      />
      <input
        :aria-label="$string('collection.price_max_slider')"
        name="range-slider-right"
        data-range-side="right"
        js-nosto-range-slider="input"
        type="range"
        min="0"
        :value="setMax"
        :max="maxCalculated"
        step="1"
        @touchend="handleRangeTouch"
      >
    </div>
  </div>
</template>

<script>

/**
 * Vue: Nosto range slider (nosto-range-slider)
 * -----------------------------------------------------------------------------
 * Slider component for displaying range based nosto filtering properties.
 *
 * @param {Object} facet - Facet object from nosto.
 * @param {Number} max - Max price.
 * @param {Number} min - Min price.
 *
 */
import { mapState } from 'vuex'
import { debounce } from '~/helpers/general'

export default {
  name: 'NostoRangeSlider',

  props: {
    facet: {
      type: Object,
      default: () => ({}),
    },
    max: {
      type: Number,
      default: 100,
    },
    min: {
      type: Number,
      default: 0,
    },
    choices: {
      type: String,
      default: '',
    },
  },

  emits: ['nosto-range-updated'],

  data() {
    return {
      setMin: 0,
      setMax: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      settings: (state) => state.index.settings,
    }),

    /**
     * Calculates an appropriate buffer above the max value.
     * @returns {Number}
     */
    maxCalculated() {
      if (this.max % 4 === 0) {
        return this.max + (this.max / 4)
      }

      let calculated = Math.ceil(this.max)

      while (calculated % 4 !== 0) {
        calculated++
      }
      return calculated + (calculated / 4)
    },

    /**
     * Calculates where the min handle should start.
     * @returns {Number}
     */
    minValue() {
      return this.maxCalculated / 5
    },

    /**
     * Return price filter.
     * @returns {Number}
     */
    priceFilter() {
      return ((this.choices.split('&')?.find(filter => filter.includes('listPrice'))?.split('=') || [])[1] || '').split('%3A') || []
    },
  },

  watch: {

    /**
     * When max is updated then set setMax.
     */
    max: {
      handler() {
        if (this.priceFilter.length) {
          return
        }

        const margin = this.maxCalculated - this.min
        this.setMax = margin < this.max ? this.max : Math.ceil(margin)
      },
      immediate: true,
    },

    /**
     * When min is set, calculated setMin.
     */
    min: {
      handler() {
        if (this.priceFilter.length) {
          return
        }

        this.setMin = Math.floor(this.min)
      },
      immediate: true,
    },

    /**
     * When choices is set, calculated range value.
     */
    choices: {
      handler() {
        this.setRangeValue()
      },
      immediate: true,
    },
  },

  created() {

    /**
     * Receives the text inputs and processes them.
     * @param {Object} event - Input event.
     */
    this.handleTextInput = debounce(event => {
      const value = event.target.value
      const terminal = event.target.getAttribute('js-nosto-range-slider')

      if (terminal === 'max-text') {
        this.validateSetMax(value)
      } else if (terminal === 'min-text') {
        this.validateSetMin(value)
      }

      this.updateRangeData()

    }, this.settings.throttle)
  },

  mounted() {
    document.querySelectorAll('input[type="range"]').forEach((input) => {
      input.addEventListener('mousedown', () => window.getSelection().removeAllRanges())
    })

    this.addRangeEventListeners()
    this.setRangeValue()
  },

  methods: {

    /**
     * Receives the text inputs and processes them.
     */
    setRangeValue() {
      const minValue = this.priceFilter[0] || this.min
      const maxValue = this.priceFilter[1] || this.max

      this.validateSetMax(maxValue)
      this.validateSetMin(minValue)
    },

    /**
     * On mobile, handle the touch end input.
     */
    handleRangeTouch() {
      if (!this.isMobile) {
        return
      }
      this.validateSetMax()
      this.validateSetMin()
      this.updateRangeData()
    },

    /**
     * Validate the input in the min value text input.
     * @param {Number} value - Input number value.
     */
    validateSetMin(value) {
      const minValue = value ?? this.setMin

      if (minValue >= Number(this.setMax)) {
        this.setMin = this.setMax
        return
      }

      if (minValue <= 0) {
        this.setMin = 0
        return
      }

      if (minValue > this.maxCalculated) {
        this.setMin = this.maxCalculated
        return
      }

      this.setMin = minValue
    },

    /**
     * Validate the input in the max value text input.
     * @param {Number} value - Input number value.
     */
    validateSetMax(value) {
      const maxValue = value ?? this.setMax

      if (maxValue <= Number(this.setMin)) {
        this.setMax = this.setMin
        return
      }

      if (maxValue <= 0) {
        this.setMax = 0
        return
      }

      if (maxValue > this.maxCalculated) {
        this.setMax = this.maxCalculated
        return
      }

      this.setMax = maxValue
    },

    /**
     * Updates the range data processed by related components.
     */
    updateRangeData() {
      this.$emit('nosto-range-updated', {
        min: this.setMin,
        max: this.setMax,
        field: `pf_${this.facet.field}`,
      })
    },

    /**
     * Adds event listeners to the range slider inputs.
     */
    addRangeEventListeners() {
      const sliders = [...document.querySelectorAll('[js-nosto-range-slider="input"]')]

      sliders.forEach(slider => {

        slider.addEventListener('input', event => {

          const target = event.target

          if (target.dataset.rangeSide === 'left') {
            if (Number(target.value) >= this.setMax) {
              this.setMax = Number(target.value)
            }
            this.setMin = Number(target.value)
          }

          if (target.dataset.rangeSide === 'right') {
            if (Number(target.value) <= this.setMin) {
              this.setMin = Number(target.value)
            }
            this.setMax = Number(target.value)
          }

          target.parentNode.style.setProperty(
            `--range-thumb-${target.dataset.rangeSide}`,
            Number(target.value),
          )

          this.updateRangeData()
        }, false)
      })
    },
  },
}
</script>

<style lang="scss">
@import './nosto-range-slider';
</style>
